import { Component, Inject, Input, OnDestroy, Optional, ViewEncapsulation } from '@angular/core';
import { ComponentBaseComponent } from '@app/core/components/component-base/component-base.component';
import { GridModalComponent } from '@app/core/components/grid/grid.decorators';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { DataTaskService } from '@app/core/services/data-task.service';
import { ModalService } from '@app/core/services/modal.service';
import { StateDialogContext, TT_STATE_DIALOG } from '@app/modal/state-modal/state-modal.component';
import { debounceTime, Subject, switchMap, from, catchError, EMPTY, groupBy, mergeMap, takeUntil } from 'rxjs';

@GridModalComponent('projectprogresslog')
@Component({
    selector: 'tt-project-progress-log',
    templateUrl: './project-progress-log.component.html',
    styleUrls: ['./project-progress-log.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class ProjectProgressLogComponent extends ComponentBaseComponent implements OnDestroy {
    @Input()
    public params?: ProjectProgressLogParams;

    public get?: ProjectProgressLogGet;

    public grid?: GridOptions;

    public saving = false;

    private change$ = new Subject<UpdateProjectProgressLogParams>();

    private destroy$ = new Subject<void>();

    constructor(
        private datatask: DataTaskService,
        private modal: ModalService,
        @Optional() @Inject(TT_STATE_DIALOG) stateDialog: StateDialogContext,
    ) {
        super();
        this.change$
            .pipe(
                takeUntil(this.destroy$),
                groupBy((e) => e.colname_changed),
                mergeMap((group$) =>
                    group$.pipe(
                        debounceTime(250),
                        switchMap((e) => from(this.updateProjectProgressLog(e)).pipe(catchError(() => EMPTY))),
                    ),
                ),
            )
            .subscribe({ next: this.onUpdateProjectProgressLogResponse });
        this.initFromDialog(stateDialog);
    }

    public onUpdateProjectProgressLogResponse = (response: Partial<ProjectProgressLogGet>) => {
        for (let k in response) {
            let key = k as keyof ProjectProgressLogGet;

            if (!!response[key] && !!this.get) this.get[key] = this.getValue('' + response[key]) as any;
        }
    };

    private getValue(value: string) {
        if (value.startsWith('base64://')) {
            try {
                let result = this.base64UrlDecode(value.substring(9));
                return result;
            } catch (error) {
                console.error(error);
                return value;
            }
        }

        return value;
    }

    public onChange(event: keyof ProjectProgressLogGet) {
        if (!this.get) return;

        this.change$.next({ ...this.get, colname_changed: event });
    }

    public async save() {
        if (!this.get) return;

        try {
            this.saving = true;

            const { errorcode, errormessage } = await this.saveProjectProgressLog(this.get);

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            } else {
                await this.init();
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.saving = false;
        }
    }

    private async loadGet() {
        if (!!this.params) {
            this.get = await this.getProjectProgressLog(this.params);
        }
    }

    private async readGrid() {
        if (!this.grid) {
            this.grid = new Grid().setRememberId('w_projectprogresslog.grid').setLoadDataFunction({ method: 3900, parameters: () => this.get });
        } else {
            this.grid.gridfunc?.read();
        }
    }

    private async saveProjectProgressLog(params: ProjectProgressLogGet): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3899, params))[0];
    }

    private async updateProjectProgressLog(params: UpdateProjectProgressLogParams): Promise<Partial<ProjectProgressLogGet>> {
        return (await this.datatask.Post(3944, params))[0];
    }

    private async getProjectProgressLog(params: ProjectProgressLogParams): Promise<ProjectProgressLogGet> {
        return (await this.datatask.Post(3898, params))[0];
    }

    private async init() {
        await this.loadGet();
        this.readGrid();
    }

    private async initFromDialog(state: StateDialogContext) {
        if ('project_keyno' in state.params) {
            this.params = state.params as unknown as ProjectProgressLogParams;
            await this.init();
            state.setHeading(this.get?.webpage_heading ?? '');
            state.setSize({ width: 'min(120rem, 95vw)', height: 'min(110rem, 95vh)' });
        }
    }

    ngOnDestroy(): void {
        //
        this.destroy$.next();
        this.destroy$.complete();
    }
}

export interface ProjectProgressLogParams {
    project_keyno: string;

    reportedprogresslog_keyno: string;
}

export interface ProjectProgressLogGet {
    webpage_heading: string;
    project_keyno: string;
    reportedprogresslog_keyno: string;
    reported_progress_pst: string | number;
    reported_progress_date: string;
    reported_progress_note: string;
    estimated_margin_pst: string | number;
    effect_of_accrual: string | number;
    accrual_calculation_snapshot: string;
    show_reported_progress_pst: '1' | '0';
    show_reported_progress_date: '1' | '0';
    show_reported_progress_note: '1' | '0';
    show_estimated_margin_pst: '1' | '0';
    show_effect_of_accrual: '1' | '0';
    show_accrual_calculation_snapshot: '1' | '0';
}

export interface UpdateProjectProgressLogParams extends ProjectProgressLogGet {
    colname_changed: keyof ProjectProgressLogGet;
}
